import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Resets a table to page 1 and drops its page param from the URL whenever the
 * search term changes, so a stale/too-high page can't trap the user on an empty
 * result.
 *
 * Prefer resetting page in the same `setSearchParams` as the search write
 * (see {@link useRoyaltiesTableState} / Titles `setFilterParam` /
 * {@link useTablePaginationState} `setSearchAndResetPage`). This hook is the
 * fallback for writers that only update the search key.
 *
 * `setPage` must be a stable setter (e.g. the dispatcher from `useState`);
 * `pageParamKey` is the URL query key that table owns.
 */
export const useResetPageOnSearchChange = (
  search: string,
  pageParamKey: string,
  setPage: (page: number) => void,
) => {
  const [, setSearchParams] = useSearchParams();
  const previousSearchRef = useRef(search);

  useEffect(() => {
    if (previousSearchRef.current === search) return;

    previousSearchRef.current = search;

    setPage(1);
    setSearchParams(
      (current) => {
        const updated = new URLSearchParams(current);

        updated.delete(pageParamKey);

        return updated;
      },
      { replace: true },
    );
  }, [search, pageParamKey, setPage, setSearchParams]);
};
